import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ThemeKey = "modern-dark" | "terminal-dark" | "terminal-amber" | "paper" | "blueprint" | "corporate-light" | "high-contrast";

const STORAGE_KEY = "commandx_theme";
const DEFAULT_THEME: ThemeKey = "modern-dark";

interface ThemeContextType {
  theme: ThemeKey;
  setTheme: (t: ThemeKey) => void;
  saving: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Apply theme to <html> immediately — no React render needed */
function applyTheme(t: ThemeKey) {
  document.documentElement.setAttribute("data-theme", t);
  localStorage.setItem(STORAGE_KEY, t);
}

// Apply stored theme before React mounts (prevents flash)
const stored = localStorage.getItem(STORAGE_KEY) as ThemeKey | null;
if (stored) applyTheme(stored);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<ThemeKey>(() => {
    return (localStorage.getItem(STORAGE_KEY) as ThemeKey) || DEFAULT_THEME;
  });
  const [saving, setSaving] = useState(false);

  // On login, read user preference from DB
  useEffect(() => {
    if (!user) return;
    supabase
      .from("user_profiles")
      .select("preferences")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        const dbTheme = (data?.preferences as any)?.theme as ThemeKey | undefined;
        if (dbTheme && dbTheme !== theme) {
          setThemeState(dbTheme);
          applyTheme(dbTheme);
        }
      });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTheme = async (t: ThemeKey) => {
    setThemeState(t);
    applyTheme(t);

    if (!user) return;
    setSaving(true);
    try {
      // Read current prefs, merge
      const { data } = await supabase
        .from("user_profiles")
        .select("preferences")
        .eq("user_id", user.id)
        .maybeSingle();
      const current = (data?.preferences as Record<string, unknown>) ?? {};
      await supabase
        .from("user_profiles")
        .update({ preferences: { ...current, theme: t } } as any)
        .eq("user_id", user.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, saving }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
